import { NEXT_ACTION_BANNER_KEY_PREFIX, TITLE_BAR_COLLAPSED_KEY, WHATS_NEW_SEEN_KEY } from '@bms2/shared';

/**
 * One guarded read and write for a preference this browser holds and no other
 * device or person shares (piece 12; T537): a folded tile, the passkey offer's
 * dismissal, the title bar's collapsed state, What's new's own seen marker,
 * the Deal Next Action banner's dismissal, the progress panel's rail state,
 * a document or report page's chosen view, the mail folder rail's state and
 * the expense lists' Rows or Tiles and Receipt column (decision 2181), the New Deal page's last type (decision 2510) and a Setup Steps guide's hidden or shown state (decision 2850). Nine copies of the same guarded
 * try/catch pair stood beside these preferences before this module; this is
 * the one.
 *
 * 🔨 A store that is absent, blocked or throws reads as `fallback`, never as
 * a thrown error: the safe way to be wrong is the screen already in front of
 * somebody, not a page that fails to render because a preference could not be
 * read. A write that fails is swallowed the same way and forgets the choice,
 * nothing more.
 *
 * 🔒 `DEVICE_PREFERENCE_KEYS` is the one list the keys and key prefixes the
 * register's nine sites hold are declared in (T537). `devicePreference.test.ts`
 * checks every reader's own key constant against it, so a copy that reaches
 * `localStorage` under a key this list does not carry is the bug this module
 * exists to end. Two readers keep their own guarded pair for now, the text
 * size in `textSize.ts` and the print preview's zoom in
 * `PrintTemplatePreview.tsx`; the WD1 review of 2026-09-29 named them and
 * they join this list when their files next change.
 */
export type PreferenceStore = Pick<Storage, 'getItem' | 'setItem'>;

export function devicePreferenceStore(): PreferenceStore | null {
  try {
    return typeof window === 'undefined' ? null : window.localStorage;
  } catch {
    // silent: a browser refusing storage forgets the choice, nothing more.
    return null;
  }
}

/** A guarded read: a missing key, a missing or throwing store, or a value `parse` refuses (returning null) all answer `fallback`. */
export function readPreference<T>(key: string, parse: (raw: string) => T | null, fallback: T, store: Pick<PreferenceStore, 'getItem'> | null = devicePreferenceStore()): T {
  try {
    const raw = store?.getItem(key);
    if (raw === null || raw === undefined) return fallback;
    const parsed = parse(raw);
    return parsed === null ? fallback : parsed;
  } catch {
    // silent: a stored preference that does not parse reads as the default, the device's fallback.
    return fallback;
  }
}

/** A guarded write: a store that throws or is absent forgets the choice, and nothing here throws past it. */
export function writePreference(key: string, value: string, store: Pick<PreferenceStore, 'setItem'> | null = devicePreferenceStore()): void {
  try {
    store?.setItem(key, value);
  } catch {
    // silent: a browser refusing to store forgets the choice, nothing more.
  }
}

// ---- Keys (T537): the fixed ones live here; a few already lived in packages/shared and stay there, imported below rather than duplicated. ----

/** A tile's folded state, per tile name (decision 0500): `Tile.tsx` templates one key per title off this prefix. */
export const TILE_FOLD_KEY_PREFIX = 'bms2.tileFolded.';

/** Whether this device has said not to offer passkey enrolment again (decision 0493). */
export const PASSKEY_OFFER_DISMISSED_KEY = 'bms2.passkeyOfferDismissed';

/** The progress panel's collapsed-to-rail state, per record type: `RecordPage.tsx` templates one key per type off this prefix (decision 1215). */
export const PROGRESS_PANEL_KEY_PREFIX = 'bms2.progressPanel.';

/** Which of a document's two views was last chosen in this browser (decision 0612). */
export const DOCUMENT_VIEW_KEY = 'bms2.documentView';

/** Which of a report's two views was last chosen in this browser (decision 0612). */
export const REPORT_VIEW_KEY = 'bms2.reportView';

/** Whether the mail client's folder rail stands open or collapsed to its glyphs in this browser (decision 1474). */
export const MAIL_FOLDER_RAIL_KEY = 'bms2.mailFolderRail';

/** Whether the Expenses tab and a report's lines draw as Rows or Tiles in this browser (decision 2181). */
export const EXPENSES_LAYOUT_KEY = 'bms2.expensesLayout';

/** Whether the expense Rows carry the Receipt column in this browser (decision 2181); off by default. */
export const EXPENSES_RECEIPT_COLUMN_KEY = 'bms2.expensesReceiptColumn';

/** 🔒 The New Deal page's last picked type, per person in this browser: `NewDealPage.tsx` templates one key per person off this prefix (decision 2510). */
export const NEW_DEAL_TYPE_KEY_PREFIX = 'bms2.newDealType.';

/** 🔒 A Setup Steps guide's hidden or shown state, per guide: `SetupSteps.tsx` templates one key per guide off this prefix (decision 2850). */
export const SETUP_STEPS_KEY_PREFIX = 'bms2.setupSteps.';

/** 🔒 A grouped list's folded group, per record type, view and group value: `ListViewer.tsx` templates one key per group off this prefix (decision 6211). */
export const LIST_GROUP_FOLD_KEY_PREFIX = 'bms2.listGroupFolded.';

/** 🔒 A Discovery card's folded group or nested block, per template and block: `DiscoveryCard.tsx` templates one key per block off this prefix (decision 6211). */
export const DISCOVERY_FOLD_KEY_PREFIX = 'bms2.discoveryFolded.';

/** 🔒 The asset register's Tiles or Board, per person in this browser: `assetView.ts` templates one key per person off this prefix (decision 12560). */
export const ASSET_VIEW_KEY_PREFIX = 'bms2.assetView.';

/** 🔒 The plant board's Expanded Cards choice, per person in this browser: `assetView.ts` templates one key per person off this prefix (decision 12831). */
export const ASSET_BOARD_EXPANDED_KEY_PREFIX = 'bms2.assetBoardExpanded.';

export const DEVICE_PREFERENCE_KEYS: readonly string[] = [
  TILE_FOLD_KEY_PREFIX,
  PASSKEY_OFFER_DISMISSED_KEY,
  PROGRESS_PANEL_KEY_PREFIX,
  DOCUMENT_VIEW_KEY,
  REPORT_VIEW_KEY,
  TITLE_BAR_COLLAPSED_KEY,
  WHATS_NEW_SEEN_KEY,
  NEXT_ACTION_BANNER_KEY_PREFIX,
  MAIL_FOLDER_RAIL_KEY,
  EXPENSES_LAYOUT_KEY,
  EXPENSES_RECEIPT_COLUMN_KEY,
  NEW_DEAL_TYPE_KEY_PREFIX,
  SETUP_STEPS_KEY_PREFIX,
  LIST_GROUP_FOLD_KEY_PREFIX,
  DISCOVERY_FOLD_KEY_PREFIX,
  ASSET_VIEW_KEY_PREFIX,
  ASSET_BOARD_EXPANDED_KEY_PREFIX,
];
